import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import axios from 'axios';

const GymMemberships = () => {
  const [memberships, setMemberships] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [members, setMembers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [formData, setFormData] = useState({
    memberId: '', packageId: '', startDate: new Date().toISOString().split('T')[0],
    fee: '', discount: 0, finalAmount: '', paymentMethod: 'cash'
  });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchMemberships();
    fetchMembers();
    fetchPackages();
  }, []);

  const fetchMemberships = async () => {
    try {
      const res = await axios.get('/api/gym/memberships');
      setMemberships(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMembers = async () => {
    try {
      const res = await axios.get('/api/gym/members');
      setMembers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPackages = async () => {
    try {
      const res = await axios.get('/api/gym/packages');
      setPackages(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePackageChange = (packageId) => {
    const pkg = packages.find(p => p.id === parseInt(packageId));
    if (pkg) {
      const fee = pkg.price;
      const finalAmount = fee - formData.discount;
      setFormData({ ...formData, packageId, fee, finalAmount });
    }
  };

  const handleDiscountChange = (discount) => {
    const finalAmount = formData.fee - discount;
    setFormData({ ...formData, discount, finalAmount });
  };

  const calculateEndDate = (startDate, duration) => {
    const start = new Date(startDate);
    start.setDate(start.getDate() + parseInt(duration));
    return start.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const pkg = packages.find(p => p.id === parseInt(formData.packageId));
      const endDate = calculateEndDate(formData.startDate, pkg.duration);
      
      const payload = {
        memberId: formData.memberId,
        packageId: formData.packageId,
        planType: 'monthly',
        startDate: formData.startDate,
        endDate: endDate,
        fee: formData.fee,
        discount: formData.discount,
        finalAmount: formData.finalAmount,
        status: 'active'
      };
      
      if (editId) {
        await axios.put(`/api/gym/memberships/${editId}`, { ...payload, id: editId });
        alert('Membership updated successfully!');
      } else {
        await axios.post('/api/gym/memberships', payload);
        
        // Create payment record
        await axios.post('/api/gym/payments', {
          memberId: formData.memberId,
          packageId: formData.packageId,
          amount: formData.finalAmount,
          paymentMethod: formData.paymentMethod,
          paymentDate: formData.startDate
        });
        alert('Membership created successfully!');
      }
      
      setShowModal(false);
      setEditId(null);
      setFormData({ memberId: '', packageId: '', planType: 'monthly', startDate: new Date().toISOString().split('T')[0], fee: '', discount: 0, finalAmount: '', paymentMethod: 'cash' });
      fetchMemberships();
    } catch (err) {
      console.error('Error saving membership:', err);
      alert('Error: ' + (err.response?.data?.error || err.message || 'Failed to save membership'));
    }
  };

  const handleEdit = (membership) => {
    setFormData({
      memberId: membership.memberId,
      packageId: membership.packageId,
      startDate: membership.startDate ? membership.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
      fee: membership.fee || 0,
      discount: membership.discount || 0,
      finalAmount: membership.finalAmount || 0,
      paymentMethod: 'cash'
    });
    setEditId(membership.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this membership?')) {
      try {
        await axios.delete(`/api/gym/memberships?id=${id}`);
        fetchMemberships();
        alert('Membership deleted!');
      } catch (err) {
        console.error('Error deleting membership:', err);
        alert('Error deleting membership: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const freezeMembership = async (id) => {
    if (window.confirm('Freeze this membership?')) {
      try {
        await axios.put(`/api/gym/memberships/${id}/freeze`);
        fetchMemberships();
        alert('Membership frozen!');
      } catch (err) {
        console.error('Error freezing membership:', err);
        alert('Error freezing membership: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const unfreezeMembership = async (id) => {
    if (window.confirm('Unfreeze this membership?')) {
      try {
        await axios.put(`/api/gym/memberships/${id}/unfreeze`);
        fetchMemberships();
        alert('Membership unfrozen!');
      } catch (err) {
        console.error('Error unfreezing membership:', err);
        alert('Error unfreezing membership: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  const getStatusBadge = (status) => {
    const variants = { active: 'success', expired: 'danger', frozen: 'warning' };
    return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col><h2>💳 Gym Memberships</h2><p className="text-muted">Member ko package assign karein</p></Col>
        <Col xs="auto">
          <Button variant="success" onClick={() => { setShowModal(true); setEditId(null); setFormData({ memberId: '', packageId: '', startDate: new Date().toISOString().split('T')[0], fee: '', discount: 0, finalAmount: '', paymentMethod: 'cash' }); }}>
            <FaPlus className="me-2" /> New Membership
          </Button>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Member</th>
                <th>Package</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Fee</th>
                <th>Discount</th>
                <th>Final Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {memberships.map((membership) => (
                <tr key={membership.id}>
                  <td><strong>{membership.member?.name}</strong><br/><small>{membership.member?.phone}</small></td>
                  <td><strong>{membership.package?.name}</strong><br/><small>{membership.package?.duration} days</small></td>
                  <td>{new Date(membership.startDate).toLocaleDateString()}</td>
                  <td>{new Date(membership.endDate).toLocaleDateString()}</td>
                  <td>Rs. {membership.fee}</td>
                  <td>Rs. {membership.discount || 0}</td>
                  <td><strong>Rs. {membership.finalAmount}</strong></td>
                  <td>{getStatusBadge(membership.status)}</td>
                  <td>
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleEdit(membership)}><FaEdit /></Button>
                    {membership.status === 'active' ? (
                      <Button variant="outline-warning" size="sm" className="me-1" onClick={() => freezeMembership(membership.id)}>Freeze</Button>
                    ) : membership.status === 'frozen' ? (
                      <Button variant="outline-success" size="sm" className="me-1" onClick={() => unfreezeMembership(membership.id)}>Unfreeze</Button>
                    ) : null}
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(membership.id)}><FaTrash /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editId ? 'Edit' : 'New'} Membership</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Select Member *</Form.Label>
              <Form.Select required value={formData.memberId} onChange={(e) => setFormData({ ...formData, memberId: e.target.value })}>
                <option value="">Choose Member</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name} - {m.phone}</option>)}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Select Package *</Form.Label>
              <Form.Select required value={formData.packageId} onChange={(e) => handlePackageChange(e.target.value)}>
                <option value="">Choose Package</option>
                {packages.map(p => <option key={p.id} value={p.id}>{p.name} - Rs. {p.price} ({p.duration} days)</option>)}
              </Form.Select>
              <Form.Text className="text-muted">Package duration will set membership end date automatically</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Start Date *</Form.Label>
              <Form.Control type="date" required value={formData.startDate} onChange={(e) => setFormData({ ...formData, startDate: e.target.value })} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Fee *</Form.Label>
              <Form.Control type="number" required value={formData.fee} readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Discount</Form.Label>
              <Form.Control type="number" value={formData.discount} onChange={(e) => handleDiscountChange(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Final Amount *</Form.Label>
              <Form.Control type="number" required value={formData.finalAmount} readOnly />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Payment Method *</Form.Label>
              <Form.Select required value={formData.paymentMethod} onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="online">Online</option>
              </Form.Select>
            </Form.Group>
            <Button type="submit" variant="success">{editId ? 'Update' : 'Create'} Membership</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default GymMemberships;
